/**
 * Fund Detail Hook
 *
 * Fetches a single fund by ISIN with its rank history using the Hono RPC client
 * with TanStack Query for caching and state management.
 */

import { useQuery, useQueryClient, queryOptions } from "@tanstack/react-query";
import { api, unwrapResponse, ApiError } from "@/lib/api";
import type { Fund, FundIsin, RankHistoryEntry } from "@curie/shared";

// ============================================================================
// Types
// ============================================================================

/**
 * Fund detail data from API
 */
export interface FundDetailData {
  /** The fund entity */
  fund: Fund;
  /** Historical rank/rating data */
  rankHistory: RankHistoryEntry[];
}

// ============================================================================
// Query Key Factory
// ============================================================================

/**
 * Query key factory for fund detail queries
 * Provides consistent key structure for cache management
 */
export const fundDetailKeys = {
  /** Root key for all fund detail queries */
  all: ["fund-detail"] as const,
  /** Key for a single fund detail */
  detail: (isin: string) => [...fundDetailKeys.all, isin] as const,
} as const;

// ============================================================================
// Query Options Factory
// ============================================================================

/**
 * Creates query options for fetching a single fund by ISIN
 * Can be used with useQuery, prefetching, or useSuspenseQuery
 *
 * @param isin - The fund ISIN to fetch
 * @returns TanStack Query options object
 *
 * @example
 * ```tsx
 * // Use with useQuery
 * const { data } = useQuery(fundDetailQueryOptions("INF123K01234"));
 *
 * // Use with prefetching
 * queryClient.prefetchQuery(fundDetailQueryOptions("INF123K01234"));
 * ```
 */
export function fundDetailQueryOptions(isin: FundIsin | string) {
  return queryOptions({
    queryKey: fundDetailKeys.detail(isin),
    queryFn: async (): Promise<FundDetailData> => {
      const response = await api.v1.funds[":isin"].$get({
        param: { isin },
      });
      return unwrapResponse<FundDetailData>(response);
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
    // Enable retry for transient failures
    retry: (failureCount, error) => {
      // Don't retry on 404s
      if (error instanceof ApiError && error.status === 404) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook for fetching a single fund by ISIN
 *
 * @param isin - The fund ISIN to fetch
 * @returns TanStack Query result with fund data, loading state, and error handling
 *
 * @example
 * ```tsx
 * function FundDetailPage({ fundId }: { fundId: string }) {
 *   const {
 *     data,
 *     isLoading,
 *     isError,
 *     error,
 *     refetch
 *   } = useFundDetail(fundId);
 *
 *   if (isLoading) return <FundDetailSkeleton />;
 *   if (isError) return <ErrorDisplay error={error} onRetry={refetch} />;
 *
 *   return (
 *     <div>
 *       <FundHeader fund={data.fund} />
 *       <RankHistory history={data.rankHistory} />
 *     </div>
 *   );
 * }
 * ```
 */
export function useFundDetail(isin: FundIsin | string) {
  return useQuery({
    ...fundDetailQueryOptions(isin),
    // Only run query if isin is provided
    enabled: Boolean(isin),
  });
}

// ============================================================================
// Cache Invalidation Helpers
// ============================================================================

/**
 * Hook that provides cache invalidation utilities for fund detail data
 *
 * @returns Object with invalidation functions
 *
 * @example
 * ```tsx
 * function RefreshButton({ isin }: { isin: string }) {
 *   const { invalidateFundDetail } = useFundDetailInvalidation();
 *
 *   const handleRefresh = async () => {
 *     await invalidateFundDetail(isin);
 *   };
 *
 *   return <Button onClick={handleRefresh}>Refresh</Button>;
 * }
 * ```
 */
export function useFundDetailInvalidation() {
  const queryClient = useQueryClient();

  return {
    /**
     * Invalidates a specific fund's cache
     * This will refetch the data on next access
     */
    invalidateFundDetail: (isin: string) =>
      queryClient.invalidateQueries({
        queryKey: fundDetailKeys.detail(isin),
      }),

    /**
     * Invalidates all fund detail cache
     * Useful after bulk operations
     */
    invalidateAllFundDetails: () =>
      queryClient.invalidateQueries({
        queryKey: fundDetailKeys.all,
      }),

    /**
     * Refetches a specific fund immediately
     * Use when you need the data right away
     */
    refetchFundDetail: (isin: string) =>
      queryClient.refetchQueries({
        queryKey: fundDetailKeys.detail(isin),
      }),

    /**
     * Pre-fetches a fund into the cache
     * Useful for optimistic UI when navigating
     */
    prefetchFundDetail: (isin: string) =>
      queryClient.prefetchQuery(fundDetailQueryOptions(isin)),

    /**
     * Updates the fund data in the cache optimistically
     * Useful for immediate UI updates before server confirmation
     */
    setFundDetailData: (isin: string, data: FundDetailData) =>
      queryClient.setQueryData(fundDetailKeys.detail(isin), data),

    /**
     * Gets the current cached fund data
     * Returns undefined if not in cache
     */
    getFundDetailData: (isin: string) =>
      queryClient.getQueryData<FundDetailData>(fundDetailKeys.detail(isin)),
  };
}

// ============================================================================
// Error Handling Utilities
// ============================================================================

/**
 * Type guard for ApiError
 */
export function isFundDetailApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/**
 * Checks if error is a 404 not found
 */
export function isFundNotFoundError(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}

/**
 * Get user-friendly error message for fund detail errors
 */
export function getFundDetailErrorMessage(error: unknown): string {
  if (isFundDetailApiError(error)) {
    if (error.status === 404) {
      return "Fund not found. It may have been removed or the ISIN is invalid.";
    }
    if (error.status >= 500) {
      return "Server error. Please try again later.";
    }
    return error.message;
  }

  if (error instanceof Error) {
    if (error.message.includes("fetch")) {
      return "Network error. Please check your connection.";
    }
    return error.message;
  }

  return "An unexpected error occurred while loading the fund.";
}
